import React, {
  DetailedHTMLProps,
  InputHTMLAttributes,
  ReactNode,
  forwardRef,
  useMemo,
} from 'react';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps } from '@react-aria/utils';
import classnames from 'classnames';
import { CheckIcon } from '@stpkg/stand-react';

export type RSBorderedListItemCollapsibleCheckRadioProps = DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  leftContent: ReactNode;
  rightContent?: ReactNode;
  additionalContent?: ReactNode;
  details: ReactNode;
  isCollapsed: boolean;
  isError?: boolean;
};

export const RSBorderedListItemCollapsibleCheckRadio = forwardRef<
  HTMLInputElement,
  RSBorderedListItemCollapsibleCheckRadioProps
>(
  (
    {
      leftContent,
      rightContent,
      additionalContent,
      details,
      isCollapsed,
      isError,
      ...props
    },
    ref,
  ) => {
    const { disabled } = props;

    const { isFocusVisible, focusProps } = useFocusRing();

    const color = '#0066FF';
    const colorTint = '#F1F7FF';

    const contentBgColorClasses = useMemo(() => {
      if (disabled) {
        return ['bg-gray-10'];
      }

      if (isError) {
        return ['bg-red-10'];
      }

      return ['bg-white'];
    }, [isError, disabled]);

    const contentRingSizeClasses = useMemo(() => {
      if (disabled) {
        return [];
      }

      if (isError) {
        return [!isFocusVisible ? 'ring-1' : 'ring-2'];
      }

      if (!isFocusVisible) {
        return ['peer-checked:ring-1'];
      }

      return ['ring-2'];
    }, [isError, disabled, isFocusVisible]);

    const markBgColorClasses = useMemo(() => {
      if (disabled) {
        return ['bg-gray-10'];
      }

      if (isError) {
        return ['bg-red-10'];
      }

      return ['bg-white'];
    }, [isError, disabled]);

    const markBorderColorClasses = useMemo(() => {
      if (disabled) {
        return ['border-gray-50'];
      }

      if (isError) {
        return ['border-red-700'];
      }

      return ['border-gray-200'];
    }, [isError, disabled]);

    return (
      <>
        <li
          className={classnames(
            'group',
            'list-style-none',
            'box-border',
            'block',
            'relative',
            'border-b',
            'border-t-0',
            'border-r-0',
            'border-l-0',
            'border-solid',
            'border-gray-200',
            'first-of-type:rounded-t-10',
            'last-of-type:rounded-b-10',
            'last-of-type:border-b-0',
          )}
        >
          <label
            className={classnames(
              'relative',
              !disabled ? 'cursor-pointer' : 'cursor-not-allowed',
            )}
          >
            <input
              {...mergeProps(props, focusProps)}
              ref={ref}
              type="radio"
              className={classnames(
                'input',
                'appearance-none',
                'absolute',
                'peer',
                'outline-none',
                'focus:outline-none',
              )}
            />
            <div
              className={classnames(
                'content',
                disabled && 'disabled',
                !isError && !disabled && 'normality',
                'relative',
                isFocusVisible && 'focus-visible',
                isFocusVisible && 'z-1',
                isError && 'error',
                'box-border',
                'p-2.5',
                contentBgColorClasses,
                contentRingSizeClasses,
                isError && 'ring-red-700',
                'transition',
                'group-first-of-type:rounded-t-10',
                isCollapsed && 'group-last-of-type:rounded-b-10',
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex w-full items-center justify-between">
                  <div>{leftContent}</div>
                  {rightContent && <div>{rightContent}</div>}
                </div>

                <div className="ml-1.5 flex-none">
                  <div className="block h-[20px] w-[20px]">
                    <span
                      className={classnames(
                        'mark',
                        !isError && !disabled && 'normality',
                        disabled && 'disabled',
                        isError && 'error',
                        'box-border',
                        'flex',
                        'justify-center',
                        'items-center',
                        'w-full',
                        'h-full',
                        markBgColorClasses,
                        'border-2',
                        'border-solid',
                        markBorderColorClasses,
                        'rounded-full',
                        'transition',
                      )}
                    >
                      <div className="icon-wrapper invisible h-16 w-16 transition">
                        <CheckIcon
                          className="block fill-white"
                          width="16"
                          height="16"
                          viewBox="0 0 48 48"
                        />
                      </div>
                    </span>
                  </div>
                </div>
              </div>

              {additionalContent && (
                <div className="mt-1">{additionalContent}</div>
              )}
            </div>
          </label>

          {/* TODO: トランジッションを検討 */}
          <div
            className={classnames(
              isCollapsed && 'hidden',
              'bg-gray-10',
              'border-t',
              'border-solid',
              'border-gray-200',
              'border-r-0',
              'border-l-0',
              'border-b-0',
              'group-last-of-type:rounded-b-10',
            )}
          >
            {details}
          </div>
        </li>
        <style jsx>{`
          .content.focus-visible {
            --tw-ring-color: ${color};
          }

          .content:hover:not(.disabled):not(.error) {
            background-color: ${colorTint};
          }

          .input:checked + .content.normality {
            --tw-ring-color: ${color};
            background-color: ${colorTint};
            z-index: 1;
          }

          .input:checked + .content .mark.normality {
            background-color: ${color};
            border-color: ${color};
          }

          .input:checked + .content .mark.disabled {
            background-color: rgba(205, 205, 205, 1);
            border-color: rgba(205, 205, 205, 1);
          }

          .input:checked + .content .mark.error:not(.disabled) {
            background-color: rgba(205, 0, 0, 1);
            border-color: rgba(205, 0, 0, 1);
          }

          .input:checked + .content .mark .icon-wrapper {
            visibility: visible;
          }
        `}</style>
      </>
    );
  },
);
